<!--
  Program: Date Picker
  Author: SDL
  Brief: Date Picker Component
-->
<template>
  <fragment v-if="lazyLoad">
    <datepicker
      v-if="!period"
      v-bind="$attrs"
      :ref="refKey"
      :bootstrap-styling="true"
      :wrapper-class="wrapClass"
      :language="currentLang"
      :format="customFormatter"
      :minimum-view="minimumView"
      :disabled-dates="disabledDate"
      :disabled="disabled"
      @input="updateDate"
    >
      <div slot="afterDateInput" class="input-group-append">
        <button
          class="btn btn-outline-secondary"
          type="button"
          :data-ref="refKey"
          :disabled="disabled"
          @click="openPicker(refKey)"
          v-click-outside="outsideClick"
        >
          <em class="icon-calendar"></em>
        </button>
      </div>
    </datepicker>
    <fragment v-else>
      <datepicker
        v-model="startDate"
        :ref="startRefKey"
        :bootstrap-styling="true"
        :wrapper-class="wrapClass"
        :language="currentLang"
        :format="customFormatter"
        :minimum-view="minimumView"
        :disabled-dates="startDisabledDates"
        :disabled="startDisabled || disabled"
        :placeholder="startPlaceHolder"
        @input="updatePeriod"
      >
        <div slot="afterDateInput" class="input-group-append">
          <button
            class="btn btn-outline-secondary"
            type="button"
            :data-ref="startRefKey"
            :disabled="startDisabled || disabled"
            @click="openPicker(startRefKey)"
            v-click-outside="outsideClick"
          >
            <em class="icon-calendar"></em>
          </button>
        </div>
      </datepicker>
      <span class="ml-2 mr-2 align-items-center">~</span>
      <datepicker
        v-model="endDate"
        :ref="endRefKey"
        :bootstrap-styling="true"
        :wrapper-class="wrapClass"
        :language="currentLang"
        :format="customFormatter"
        :minimum-view="minimumView"
        :disabled-dates="endDisabledDates"
        :disabled="endDisabled || disabled"
        :placeholder="endPlaceHolder"
        @input="updatePeriod"
      >
        <div slot="afterDateInput" class="input-group-append">
          <button
            class="btn btn-outline-secondary"
            type="button"
            :data-ref="endRefKey"
            :disabled="endDisabled || disabled"
            @click="openPicker(endRefKey)"
            v-click-outside="outsideClick"
          >
            <em class="icon-calendar"></em>
          </button>
        </div>
      </datepicker>
    </fragment>
  </fragment>
</template>
<script>
import Datepicker from 'vuejs-datepicker';
import { mapGetters } from 'vuex';
import moment from 'moment';
import $ from 'jquery';
import _ from 'lodash';
import SDLUtil from '@/utils/SDLUtil';

export default {
  components: {
    Datepicker,
  },
  props: {
    wrapClass: {
      required: false,
      type: String,
      default: 'wd150',
    },
    // period information
    period: {
      required: false,
      type: Object,
    },
    // minimum unit - ex) When setting to month, picker can be selected on a monthly basis
    minimumView: {
      required: false,
      type: String,
      default: 'day',
    },
    default: {
      required: false,
      type: Boolean,
      default: true,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
    disabledDate: {
      required: false,
      type: Object,
      default: () => {},
    },
    startDisabledDate: {
      required: false,
      type: String,
      default: '',
    },
    endDisabledDate: {
      required: false,
      type: String,
      default: '',
    },
  },
  data() {
    return {
      refKey: _.uniqueId('dp_'),
      startRefKey: _.uniqueId('start_dp_'),
      endRefKey: _.uniqueId('end_dp_'),
      lazyLoad: false,
      startDate: '',
      endDate: '',
      currentLang: null,
      lang: {},
    };
  },
  created() {
    import('vuejs-datepicker/dist/locale/').then(data => {
      this.$i18n.availableLocales.forEach(countryCode => {
        const country = countryCode.replace('_', '-').split('-')[0];
        this.lang[countryCode] = data[country];
      });
      // Applying the language currently set
      this.currentLang = this.lang[this.$i18n.locale];
      // period information initialization
      if (this.period) {
        this.startDate = _.has(this.period, 'startDate')
          ? this.period.startDate
          : moment().subtract(1, 'M').format('YYYY-MM-DD');
        this.endDate = _.has(this.period, 'endDate')
          ? this.period.endDate
          : moment().format('YYYY-MM-DD');
      } else {
        this.startDate = '';
        this.endDate = '';
      }
      if (this.period) {
        this.period.startDate = this.startDate;
        this.period.endDate = this.endDate;
      }
      this.lazyLoad = true;
    });
  },
  computed: {
    ...mapGetters({
      locale: 'getLocale',
    }),
    // Start Date Zone Disabled Settings
    startDisabled() {
      return this.period.startDisabled;
    },
    // End Date Zone Disabled Settings
    endDisabled() {
      return this.period.endDisabled;
    },
    // Start date range settings.
    startDisabledDates() {
      return {
        to: moment(this.startDisabledDate).toDate(),
        from: moment(this.endDate)
          .parseZone()
          .toDate(),
      };
    },
    // End date range settings.
    endDisabledDates() {
      return {
        to: moment(this.startDate).toDate(),
      };
    },
    // Start Date PlaceHolder
    startPlaceHolder() {
      return this.period.startPlaceHolder || '';
    },
    // End Date PlaceHolder
    endPlaceHolder() {
      return this.period.endPlaceHolder || '';
    },
    // Date Format by Locale
    dateFormat() {
      return this.minimumView === 'month'
        ? SDLUtil.getMsgProp('data-format.date.ym')
        : SDLUtil.getMsgProp('data-format.date.ymd');
    },
  },
  watch: {
    // Change of date unit
    minimumView() {
      this.updatePeriod();
    },
    // locale Change
    locale() {
      this.currentLang = this.lang[this.locale];
    },
    disabledDate(value) {
      this.disabledDate = value;
    },
  },
  methods: {
    /**
     * param: param -> Object
     * return: void
     * exception:
     * description : Close when clicking outside the calendar button area
     */
    outsideClick(param) {
      if (_.find(param.evt.path, ['className', 'vdp-datepicker__calendar'])) {
        return;
      }
      if (
        param.evt.target.className.indexOf('prev') > -1
        || param.evt.target.className.indexOf('next') > -1
        || param.evt.target.className.indexOf('day__month_btn') > -1
        || param.evt.target.className.indexOf('month__year_btn') > -1
        || (param.evt.target.nodeName === 'SPAN' && param.evt.target.parentNode.nodeName === 'HEADER')
      ) {
        return;
      }
      const calendar = this.$refs[$(param.source).data('ref')];
      if (calendar.isOpen) calendar.close(true);
    },
    /**
     * param: date -> Date
     * return: String
     * exception:
     * description : Return to set date format String
     */
    customFormatter(date = moment()) {
      return moment(date).format(this.dateFormat);
    },
    /**
     * param: refKey -> String
     * return: String
     * exception:
     * description : Activate the calendar by clicking the calendar button
     */
    openPicker(refKey) {
      this.$refs[refKey].showCalendar();
    },
    /**
     * param: value -> Date
     * return: String
     * exception:
     * description : When changing value updates.
     */
    updateDate(value) {
      this.$emit('input', moment(value).format('YYYY-MM-DD'));
      this.$emit('moment', moment(value));
    },
    /**
     * param: value -> Date
     * return: String
     * exception:
     * description : Start date change
     */
    updateStartDate(value) {
      this.startDate = this.customFormatter(value);
      this.updatePeriod();
    },
    /**
     * param: value -> Date
     * return: String
     * exception:
     * description : End date change
     */
    updateEndDate(value) {
      this.endDate = this.customFormatter(value);
      this.updatePeriod();
    },
    /**
     * param:
     * return: String
     * exception:
     * description : When changing the period updates
     */
    updatePeriod() {
      if (this.startDate.length === 0 && this.endDate.length === 0) {
        return;
      }
      this.period.startDate = moment(this.startDate).format('YYYY-MM-DD');
      this.period.endDate = moment(this.endDate).format('YYYY-MM-DD');
      this.$emit('input', { ...this.period });
      this.$emit('moment', {
        startDate: moment(this.period.startDate).format('YYYY-MM-DD'),
        endDate: moment(this.period.endDate).format('YYYY-MM-DD'),
      });
    },
  },
};
</script>

<style>
.wd150 {
  width: 150px !important;
}
.vdp-datepicker__calendar {
  z-index: 118 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/common/control/datepicker/DatePicker.vue